import React, { useState } from 'react';
import { Edit3, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, ToolActionButtons, NoteCard } from '../shared';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const FillPDFForms = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        date: new Date().toLocaleDateString()
    });

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPageCount(pdf.getPageCount());
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const fillForm = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

            setProgress(50);

            const form = pdfDoc.getForm();
            const fields = form.getFields();

            // Try to fill form fields if they exist
            fields.forEach(field => {
                const fieldName = field.getName().toLowerCase();
                if (fieldName.includes('name') && formData.name) {
                    try {
                        const textField = form.getTextField(field.getName());
                        textField.setText(formData.name);
                    } catch (e) { }
                }
                if (fieldName.includes('email') && formData.email) {
                    try {
                        const textField = form.getTextField(field.getName());
                        textField.setText(formData.email);
                    } catch (e) { }
                }
                if (fieldName.includes('date') && formData.date) {
                    try {
                        const textField = form.getTextField(field.getName());
                        textField.setText(formData.date);
                    } catch (e) { }
                }
            });

            setProgress(90);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            saveAs(blob, `${fileName}_filled.pdf`);

            toast.success('Form filled successfully!');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to fill form: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <Edit3 size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Fill PDF Forms
                </h1>
                <p className="tool-description">
                    Automatically fill PDF form fields with your data.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF form here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPageCount(0); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Form Data
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                                <div>
                                    <label className="label">Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="input"
                                        placeholder="Enter your name"
                                    />
                                </div>

                                <div>
                                    <label className="label">Email</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="input"
                                        placeholder="Enter your email"
                                    />
                                </div>

                                <div>
                                    <label className="label">Date</label>
                                    <input
                                        type="text"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="input"
                                    />
                                </div>
                            </div>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button variant="primary" size="lg" onClick={fillForm} disabled={isProcessing} loading={isProcessing} fullWidth>
                            <Download size={20} />
                            Fill Form & Download
                        </Button>
                    </div>

                    <NoteCard variant="info">
                        <strong>Note:</strong> This tool will attempt to auto-fill common form fields (name, email, date).
                    </NoteCard>
                </>
            )}
        </div>
    );
};

export default FillPDFForms;
